import { trigger, transition, style, animate } from "@angular/animations";
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { map, catchError, of, forkJoin, finalize, Observable, Subject, takeUntil } from "rxjs";
import { DocumentMasterService } from "src/app/Common/DocumentMasterService/document-master.service";
import { ToastrService } from "ngx-toastr";
import { UserLoginService } from "src/app/Common/UserLoginService/userlogin.service";
import { MasterAttachmentService } from "src/app/Common/MasterAttachment/master-attachment.service";
import { MasterAttachment } from "src/app/Common/MasterAttachment/master-attachment.interface";
import { ConfirmmodalserviceService } from "../confirm-delete-modal/confirmmodalservice.service";
import { environment } from "src/environments/environment";



@Component({
  selector: "app-off-canvas",
  templateUrl: "./off-canvas.component.html",
  styleUrls: ["./off-canvas.component.css"],
  animations: [trigger('accordionAnimation', [
    transition(':enter', [
      style({ transform: 'scaleX(0)', opacity: 0, transformOrigin: 'right', overflow: 'hidden' }),
      animate('300ms ease-out', style({ transform: 'scaleX(1)', opacity: 1 }))
    ]),
    transition(':leave', [
      // Set transformOrigin first
      style({ transformOrigin: 'right', overflow: 'hidden' }),
      animate('300ms ease-in', style({ transform: 'scaleX(0)', opacity: 0 }))
    ])
  ])
  ]
})
export class OffCanvasComponent implements OnChanges, OnDestroy {
  // Suggestions load via a LIVE subscription (see loadAttachmentLabelSuggestions)
  // so a newly-added Document Name shows up immediately - torn down here.
  private destroy$ = new Subject<void>();

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Input() status: "open" | "close" | "" = ""; // panel state
  @Input() isExpanded: boolean = false;
  @Input() title: string = ""; // optional header title
  @Output() closed = new EventEmitter<void>(); // emit event when user closes
  @Output() expanded = new EventEmitter<boolean>(); // emit event when user expands/collapses
  @Input() masterType!: string;
  // The master record's own id (e.g. relationId, vendorId). Leave at 0 for a
  // record that hasn't been saved yet — see commitPendingAttachments().
  // When set (>0), this component owns attachment persistence entirely: it
  // self-loads existing ImageMaster/DocMaster rows and saves/deletes as the
  // user uploads or removes files, via MasterAttachmentService. Consumers
  // that never set this (masterId stays 0) keep the old behaviour of
  // driving existingUploadedUrls themselves and reading fileUploaded.
  @Input() masterId: number = 0;
  // Hides the Delete action on already-saved files, so a consumer can offer
  // an add-only experience (e.g. company-detail's Documents tab, where
  // removing an existing document is deliberately not exposed here). Newly
  // queued (not-yet-uploaded) files can still be removed from the queue
  // either way - that's just undoing a selection, not deleting a saved row.
  @Input() allowRemoveExisting: boolean = true;
  // Hides already-saved files from the "Add Files" upload modal entirely -
  // that modal then only ever lists what's newly queued in this session.
  // Off for company-detail's Documents tab, whose own grid is already the
  // place to see/preview/rename what's saved; this modal there is purely
  // an add flow. Existing consumers (Supplier/Vendor/... edit panels) keep
  // seeing saved files here by default since that's still their one place
  // to manage attachments.
  @Input() showExistingInUploadModal: boolean = true;
  @Input() existingUploadedUrls: string[] = [];
  // Populated by loadExistingAttachments() when masterId is set - carries
  // the source table + id needed to persist further changes correctly.
  existingAttachments: MasterAttachment[] = [];
  // Parallel to existingUploadedUrls/existingAttachments - the Document Name
  // already saved for that row (imgTitle/docDesc), editable in place.
  existingLabels: string[] = [];
  // Parallel to existingUploadedUrls/existingAttachments - whether this
  // already-saved file is included the next time "Apply to Selected" runs.
  // Defaults to checked, same as selectedFileChecked, so already-saved files
  // can be bulk-(re)named alongside newly queued ones (e.g. relabeling a
  // whole batch of previously uploaded pages) without first opening each
  // row's individual edit mode.
  existingChecked: boolean[] = [];
  // Index of the existing-file row currently showing an editable input for
  // its Document Name (null = none). Only one row edits at a time.
  editingExistingIndex: number | null = null;
  // Label typed for a url that hasn't been persisted under a real masterId
  // yet (masterId was 0 when it was uploaded) - consumed by
  // commitPendingAttachments() once the parent has a real id.
  private pendingNewLabelsByUrl: { [url: string]: string } = {};
  masterAttachmentService = inject(MasterAttachmentService);
  confirmModal = inject(ConfirmmodalserviceService);
  // Gate for the "Add Files" button — defaults true so every consumer shows
  // it unless a page explicitly turns it off. Not object-config-driven: no
  // page/tenant lookup decides this, only whatever the caller binds here.
  @Input() showDocumentUpload: boolean = true;

  // Shows an optional "Document name" field (free text + suggestion
  // dropdown) next to each queued file so the caller can label what a file
  // actually is (e.g. "GST Certificate"). Turns on automatically the moment
  // attachmentLabelSuggestions has items (see ngOnChanges) - a page that
  // binds a real suggestion list obviously wants the field shown, so there's
  // no need to *also* bind this explicitly. Still bindable directly for a
  // page that wants the field with no curated list at all (plain free text).
  // Off by default so existing consumers (e.g. material-inward-history,
  // which just wants plain upload) are unaffected either way.
  @Input() enableAttachmentLabel: boolean = false;
  // Suggestions shown in the label's dropdown - one flat, admin-managed list
  // ('DOCLABEL' ValueList) shared by every menu that turns the field on.
  // Bind attachmentLabelSuggestions to override with a page-specific list
  // instead (this alone is enough to turn the field on - see above). If
  // DOCLABEL has no rows configured yet and no override is bound, the field
  // stays a plain text input - no dropdown, nothing hardcoded.
  @Input() attachmentLabelSuggestions: string[] = [];
  // When true, attachmentLabelSuggestions is this page's ONLY source of
  // Document Name suggestions - the legacy 'DOCLABEL' ValueList is never
  // loaded or shown as a fallback, even while attachmentLabelSuggestions is
  // still empty (e.g. DocConfigMaster hasn't been configured yet for this
  // masterType, or its async read just hasn't resolved). Off by default so
  // existing consumers that rely on the DOCLABEL fallback (no page-specific
  // list bound) are unaffected. company-detail's Documents tab turns this on
  // since it's always DocConfigMaster-driven and should never silently fall
  // back to the flat tenant-wide list.
  @Input() curatedLabelSuggestionsOnly: boolean = false;
  valueListLabelSuggestions: string[] = [];
  // Shown in place of ng-select's default "No items found" whenever a typed
  // Document Name matches nothing in the list - callers out that typing is
  // still useful (via [addTag]) instead of reading like a dead end.
  readonly docNameNotFoundText = 'No matching document name found. If the document name is not in the list, type the name to add';
  // Replaces ng-select's default addTag prompt ('Add item "demo"') with
  // wording that doesn't overpromise: a curated list (DocConfigMaster) never
  // persists what's typed here - see onAddDocumentName below - it's only
  // ever this one attachment's label, admin-curated lists aren't editable
  // from an upload modal. Only the legacy DOCLABEL path (no page-specific
  // list bound) actually saves it as a future suggestion.
  get addDocNameTagText(): string {
    return this.curatedLabelSuggestionsOnly
      ? 'Use as Document Name:'
      : 'Add new suggestion:';
  }

  @ViewChild('uploadModal', { static: false }) uploadModal!: any;

  uploadedFilesCount: number = 0;
  uploadModalRef: any;

  selectedFiles: File[] = [];
  selectedFilePreviews: string[] = [];
  // Parallel to selectedFiles - the optional label the user typed/picked for that file.
  selectedFileLabels: string[] = [];
  // Parallel to selectedFiles - whether this file is included the next time
  // "Apply to Selected" runs. Defaults to checked so the bulk bar behaves
  // like a plain "Apply to All" out of the box; unchecking a row lets a user
  // uploading several different document types in one batch (e.g. 2 PDFs
  // that are one doc type + 1 Excel that's another) name each group
  // separately instead of stamping every queued file with the same name.
  selectedFileChecked: boolean[] = [];
  // "Apply to Selected" bar above the queue - fills every checked file's
  // selectedFileLabels slot at once for a batch of same-purpose files (e.g.
  // 6-7 pages of one agreement), while each file's own input stays editable
  // afterward.
  bulkAttachmentLabel: string = '';

  uploadedUrls: string[] = [];
  imageUploadLoading = false;
  modalService = inject(NgbModal)
  documentService = inject(DocumentMasterService);
  Toaster = inject(ToastrService)
  User = inject(UserLoginService).User()

  @Output() fileUploaded = new EventEmitter<{
    newUrls: string[];
    allUrls: string[];
    // Parallel to newUrls/allUrls - '' when enableAttachmentLabel is off or
    // the user left the field blank.
    newLabels: string[];
    allLabels: string[];
  }>();

  ngOnInit() {
    if (this.enableAttachmentLabel && !this.curatedLabelSuggestionsOnly && this.attachmentLabelSuggestions.length === 0) {
      this.loadAttachmentLabelSuggestions();
    }
  }

  // Loads (or clears) existing attachments as soon as masterId is set/changes
  // - not just when the upload modal is opened. Without this, opening
  // "Update Supplier" never calls ImageMaster/DocMaster ReadByMasterId at
  // all until the user clicks "Add Files", and totalFilesCount (the badge
  // on that button) stays wrong until then.
  ngOnChanges(changes: SimpleChanges) {
    // A page binding its own attachmentLabelSuggestions at all (e.g.
    // DocConfigMaster-backed lists on Supplier/Vendor/Customer/Agency) is
    // reason enough to show the field - checked on the binding itself
    // (changes['attachmentLabelSuggestions'] fires once as soon as the
    // template binds it, even while the array is still empty because its
    // async load hasn't resolved yet, or because that masterType has no
    // DocConfigMaster rows configured at all) rather than gating on
    // .length > 0. Gating on length used to leave the Document Name field,
    // "Apply to Selected" bar and per-file checkboxes invisible on every
    // page whose DocConfigMaster list started (or stayed) empty - only ever
    // turns it ON here, never off, so an explicit [enableAttachmentLabel]=
    // "false"-style opt-out (there isn't one today, but if a page ever needs
    // it) still wins.
    if (changes['attachmentLabelSuggestions']) {
      this.enableAttachmentLabel = true;
    }
    if (changes['masterId'] && !changes['masterId'].firstChange) {
      this.existingAttachments = [];
      this.existingUploadedUrls = [];
      this.existingLabels = [];
      this.existingChecked = [];
      this.editingExistingIndex = null;
      if (this.masterId && this.masterType) {
        this.loadExistingAttachments();
      }
    }
  }

  // Suggestions ('DOCLABEL' ValueList) - centralized in MasterAttachmentService
  // so every consumer of the Document Name field (this list, the
  // company-detail Documents tab's rename modal, ...) reads the same source.
  // Live subscription (not a one-shot read) - it re-emits on its own the
  // moment onAddDocumentName's create resolves, same reactive pattern the
  // UIC form engine's ValuelistSelectorComponent uses; torn down in
  // ngOnDestroy since it never completes by itself.
  private loadAttachmentLabelSuggestions() {
    this.masterAttachmentService.getDocumentNameSuggestions(
      this.User.companyId,
      this.User.loggedInBranchId
    ).pipe(takeUntil(this.destroy$)).subscribe(suggestions => {
      this.valueListLabelSuggestions = suggestions;
    });
  }

  get effectiveAttachmentLabelSuggestions(): string[] {
    if (this.curatedLabelSuggestionsOnly) return this.attachmentLabelSuggestions;
    return this.attachmentLabelSuggestions.length
      ? this.attachmentLabelSuggestions
      : this.valueListLabelSuggestions;
  }

  // Bound to every Document Name ng-select's [addTag] - called when the user
  // picks the "Use as Document Name" / "Add new suggestion" tag row (or just
  // presses Enter on it, same as the ValuelistSelectorComponent's own
  // addTag - see that component for the reference pattern this mirrors).
  // Any free-text name is always accepted as the label; whether it also gets
  // *persisted* as a future suggestion depends on the source: a curated,
  // page-specific list (DocConfigMaster) is admin-managed and never gets
  // written to from here - a typed name is only ever this one attachment's
  // label, the dropdown stays exactly what the admin configured. Only the
  // legacy shared 'DOCLABEL' ValueList path (no page-specific list bound at
  // all) persists what's typed, so every consumer of that flat list keeps
  // seeing it as a future suggestion.
  //
  // Deliberately does NOT `return` the create call in the persisted branch.
  // ng-select's selectTag() only knows how to resolve a Promise or a plain
  // value from [addTag] - it checks `tag instanceof Promise`, which an RxJS
  // Observable never is, so returning the Observable directly (as this used
  // to) made ng-select treat the live Observable object itself as the item
  // to select instead of awaiting it, and the create call was never even
  // subscribed to. Returning nothing sidesteps that entirely: selectTag()
  // then does nothing on its own, and this function's own subscribe callback
  // below sets the model once the save actually completes.
  onAddDocumentName = (term: string) => {
    const field = this.activeDocNameField;
    const trimmed = term.trim();
    const setValue = (value: string) => {
      if (!field) return;
      if (field.kind === 'bulk') {
        this.bulkAttachmentLabel = value;
      } else if (field.kind === 'existing' && field.index !== null) {
        this.existingLabels[field.index] = value;
      } else if (field.kind === 'new' && field.index !== null) {
        this.selectedFileLabels[field.index] = value;
      }
    };

    if (this.curatedLabelSuggestionsOnly || this.attachmentLabelSuggestions.length) {
      setValue(trimmed);
      return;
    }

    this.masterAttachmentService.addDocumentNameSuggestion(
      term,
      this.User.companyId,
      this.User.loggedInBranchId,
      this.User.userId.toString()
    ).subscribe(savedName => setValue((savedName || term).trim()));
  };

  // Which Document Name field is currently being typed in - set from
  // onDocNameSearch below (fired on every keystroke, well before Enter/blur)
  // so onAddDocumentName above knows which field's model to update once its
  // async save resolves, since [addTag] itself is one shared function
  // reused across every field on this component.
  private activeDocNameField: { kind: 'bulk' | 'existing' | 'new'; index: number | null } | null = null;

  // Lets a user just type a Document Name and move on, without having to
  // pick a suggestion or explicitly click ng-select's "Add item" option -
  // it shouldn't be mandatory to add every one-off typed name to the shared
  // suggestion list just to use it. ng-select clears its own internal
  // searchTerm as soon as the dropdown closes (bound to a document
  // 'mousedown' listener, which fires before this field's blur event), so
  // the in-progress term is captured here from (search) instead, and
  // applied directly to the field's own model on (blur) - but only if
  // nothing was actually selected/added in the meantime (see
  // onDocNameSelected, wired to ng-select's (change)).
  private typedDocNames: { [key: string]: string } = {};

  private docNameKey(kind: 'bulk' | 'existing' | 'new', index: number | null): string {
    return index === null ? kind : `${kind}-${index}`;
  }

  onDocNameSearch(kind: 'bulk' | 'existing' | 'new', index: number | null, term: string) {
    this.activeDocNameField = { kind, index };
    this.typedDocNames[this.docNameKey(kind, index)] = term;
  }

  onDocNameSelected(kind: 'bulk' | 'existing' | 'new', index: number | null) {
    delete this.typedDocNames[this.docNameKey(kind, index)];
  }

  commitTypedDocName(kind: 'bulk' | 'existing' | 'new', index: number | null) {
    const key = this.docNameKey(kind, index);
    const typed = (this.typedDocNames[key] || '').trim();
    delete this.typedDocNames[key];
    if (!typed) return;

    if (kind === 'bulk' && typed !== (this.bulkAttachmentLabel || '').trim()) {
      this.bulkAttachmentLabel = typed;
    } else if (kind === 'existing' && index !== null && typed !== (this.existingLabels[index] || '').trim()) {
      this.existingLabels[index] = typed;
    } else if (kind === 'new' && index !== null && typed !== (this.selectedFileLabels[index] || '').trim()) {
      this.selectedFileLabels[index] = typed;
    }
  }

  close() {
    this.closed.emit();
    this.status = 'close'; // Trigger the close animation
    setTimeout(() => {
      this.status = ''; // Hide element after animation completes
    }, 250); // Match the animation duration (300ms)
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.expanded.emit(this.isExpanded);
  }

  openUploadModal() {
    this.selectedFiles = [];
    this.selectedFilePreviews = [];
    this.selectedFileLabels = [];
    this.selectedFileChecked = [];
    this.bulkAttachmentLabel = '';
    this.uploadedUrls = [];
    // Unconditional, every open - without this, existingAttachments/
    // existingUploadedUrls keep whatever the PREVIOUS session (a different
    // supplier, or none at all for a brand-new one) left behind, since
    // loadExistingAttachments() below only reassigns them when masterId is
    // truthy. Switching from "Edit Supplier A" to "New Supplier" (masterId
    // 0) or to "Edit Supplier B" would otherwise keep showing A's files.
    this.existingAttachments = [];
    this.existingUploadedUrls = [];
    this.existingLabels = [];
    this.existingChecked = [];
    this.editingExistingIndex = null;

    if (!this.masterType) {
      console.error('MasterType missing');
      return;
    }

    if (this.masterId) {
      this.loadExistingAttachments();
    }

    this.uploadModalRef = this.modalService.open(this.uploadModal, {
      size: 'lg',
      backdrop: 'static',
      scrollable: true
    });
  }

  async onFilesSelected(event: any) {
    const files = event.target.files;
    if (!files?.length) return;

    for (let file of files) {
      const processed = await this.compressImage(file);
      this.selectedFiles.push(processed);
      this.selectedFileLabels.push('');
      this.selectedFileChecked.push(true);

      const reader = new FileReader();
      reader.onload = () => {
        this.selectedFilePreviews.push(reader.result as string);
      };
      reader.readAsDataURL(processed);
    }
  }

  private compressImage(file: File, maxDim = 1920, quality = 0.82): Promise<File> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }
      const img = new Image();
      const blobUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        let { width, height } = img;
        if (width <= maxDim && height <= maxDim) {
          resolve(file);
          return;
        }
        if (width > height) {
          height = Math.round(height * maxDim / width);
          width = maxDim;
        } else {
          width = Math.round(width * maxDim / height);
          height = maxDim;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob!], file.name, { type: 'image/jpeg' })),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        resolve(file);
      };
      img.src = blobUrl;
    });
  }


  removeSelectedFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.selectedFilePreviews.splice(index, 1);
    this.selectedFileLabels.splice(index, 1);
    this.selectedFileChecked.splice(index, 1);
  }

  // Already-saved files only count toward "Apply to Selected" when this
  // modal actually lists them (showExistingInUploadModal) - company-detail's
  // add-only modal keeps existingUploadedUrls populated internally (so
  // totalFilesCount stays right) but never renders those rows, so they must
  // never factor into bulk selection there either.
  private get queuedExistingCount(): number {
    return this.showExistingInUploadModal ? this.existingUploadedUrls.length : 0;
  }

  // Whether the queue currently has a leading checkbox column at all - only
  // once there's a Document Name field to apply in bulk to (enableAttachmentLabel)
  // and more than one queued file (new + already-saved combined) to choose
  // between. Drives both the per-file checkbox on every row - new files and,
  // when shown, already-saved ones alike, so a whole batch (or just the
  // saved files within it) can be renamed together - and the grid's extra
  // leading column, so columns stay aligned.
  get showBulkCheckboxColumn(): boolean {
    return this.enableAttachmentLabel && (this.selectedFiles.length + this.queuedExistingCount) > 1;
  }

  // Header checkbox above the queue - true only once every queued file
  // (new and, when shown, already-saved) is checked, so the toggle button
  // can read "Clear All" instead of "Select All" once nothing's left to
  // select.
  get allSelectedForBulk(): boolean {
    const total = this.selectedFiles.length + this.queuedExistingCount;
    if (total === 0) return false;
    const newOk = this.selectedFileChecked.every(c => c);
    const existingOk = !this.showExistingInUploadModal || this.existingChecked.every(c => c);
    return newOk && existingOk;
  }

  toggleSelectAllForBulk() {
    const next = !this.allSelectedForBulk;
    this.selectedFileChecked = this.selectedFiles.map(() => next);
    if (this.showExistingInUploadModal) {
      this.existingChecked = this.existingUploadedUrls.map(() => next);
    }
  }

  // "Apply to Selected" - stamps the bulk name onto every currently CHECKED
  // queued file, new AND already-saved alike (e.g. relabeling a whole batch
  // of previously uploaded pages, or naming a 6-7 page agreement upload in
  // one go, or just one document-type group out of a mixed batch), while
  // each file's own input further down stays editable for anyone who wants a
  // different name for just one file. Already-saved rows persist their new
  // label the same way any other edit to existingLabels does - on the
  // modal's own Submit, via saveChangedExistingLabels().
  applyBulkAttachmentInfo() {
    if (!this.bulkAttachmentLabel) return;
    this.selectedFiles.forEach((_, i) => {
      if (this.selectedFileChecked[i]) {
        this.selectedFileLabels[i] = this.bulkAttachmentLabel;
      }
    });
    if (this.showExistingInUploadModal) {
      this.existingUploadedUrls.forEach((_, i) => {
        if (this.existingChecked[i]) {
          this.existingLabels[i] = this.bulkAttachmentLabel;
        }
      });
    }
  }


  uploadFiles() {
    if (this.selectedFiles.length === 0) {
      // No new files queued - the only thing "Save Changes" might need to
      // do is persist any Document Name edits made to already-saved rows
      // (see cancelEditExisting() - editing no longer saves per-row; it's
      // batched here with everything else on submit).
      this.saveChangedExistingLabels().subscribe({
        next: () => {
          this.editingExistingIndex = null;
          if (this.masterId) this.loadExistingAttachments();
          this.emitFileUploaded([], []);
          this.uploadModalRef?.close();
        },
        error: (err) => {
          console.error('Failed to save document name changes', err);
          this.emitFileUploaded([], []);
          this.uploadModalRef?.close();
        }
      });
      return;
    }




    this.imageUploadLoading = true;
    this.uploadedUrls = [];

    // Upload each file using DocumentMaster_Upload -> returns DocumentMasterDTO with url
    const uploadRequests = this.selectedFiles.map(file => {
      // Same multipart fields item-basic-details sends - the upload API needs
      // the tenant context in the form body, not just the request headers.
      const fd = new FormData();
      fd.append('File', file, file.name);
      fd.append('TenantId', this.User.companyId.toString());
      fd.append('TenantBranchId', this.User.loggedInBranchId.toString());
      fd.append('SoftwareId', environment.SoftwareId);
      return this.documentService.DocumentMaster_Upload(fd).pipe(
        map((resp: any) => resp?.url ?? resp?.path ?? resp?.file ?? null),
        catchError(err => {
          console.error('Upload failed for file', file.name, err);
          return of(null);
        })
      );
    });

    forkJoin(uploadRequests).pipe(
      finalize(() => { /* continue */ })
    ).subscribe({
      next: (urls) => {
        // Pair each result with the label typed for that same slot before
        // dropping the failed (null) ones, so labels stay aligned to urls.
        const succeeded = (urls as Array<string | null>)
          .map((url, i) => ({ url, label: this.selectedFileLabels[i] || '' }))
          .filter((p): p is { url: string; label: string } => !!p.url);

        this.uploadedUrls = succeeded.map(p => p.url);
        succeeded.forEach(p => {
          if (p.label) this.pendingNewLabelsByUrl[p.url] = p.label;
        });

        if (this.masterId) {
          // Record already exists - persist right away instead of leaving
          // it to the parent. MasterAttachmentService decides ImageMaster
          // vs. DocMaster per file extension. Runs alongside any pending
          // Document Name edits on existing rows (same "only on Submit"
          // batching as the no-new-files branch above).
          //
          // Everything that tells the outside world "the upload is done"
          // (emitFileUploaded, the success toast, closing the modal) waits
          // for this to actually complete instead of firing right alongside
          // it. Firing them immediately used to race the parent's own
          // refresh (documents.component.ts's onAttachmentsChanged -> load())
          // against these still-in-flight Create/Update calls: with several
          // files queued, the parent's ReadByMasterId could land before the
          // backend had finished persisting all of them, so the grid showed
          // fewer documents than were actually uploaded until the next full
          // page reload (by which point every create had long since
          // committed) - the data was never actually lost, just read back
          // too early.
          forkJoin([
            this.masterAttachmentService.saveAttachmentChanges(
              this.masterType,
              this.masterId,
              [...this.existingUploadedUrls, ...this.uploadedUrls],
              this.existingAttachments,
              this.User.companyId,
              this.User.loggedInBranchId,
              this.User.userId.toString(),
              this.pendingNewLabelsByUrl
            ),
            this.saveChangedExistingLabels()
          ]).subscribe({
            next: () => {
              this.editingExistingIndex = null;
              this.loadExistingAttachments();
              this.finishUpload(succeeded);
            },
            error: (err) => {
              console.error('Failed to save attachment changes', err);
              this.imageUploadLoading = false;
              this.Toaster.error('Some files failed to save. Please try again.');
            }
          });
        } else {
          // Record not saved yet - keep the urls visible if the modal is
          // reopened; commitPendingAttachments() persists them once the
          // parent has a real id. Nothing async left to wait on here, so
          // finish immediately.
          this.existingUploadedUrls = [...this.existingUploadedUrls, ...this.uploadedUrls];
          this.finishUpload(succeeded);
        }
      },
      error: (err) => {
        console.error('Upload flow failed', err);
        this.imageUploadLoading = false;
      }
    });
  }

  // Common "the upload is done" tail - emits fileUploaded (so the parent's
  // own refresh only ever starts once persistence has actually committed),
  // shows the success toast, and resets/closes the modal.
  private finishUpload(succeeded: Array<{ url: string; label: string }>) {
    this.emitFileUploaded(this.uploadedUrls, succeeded.map(p => p.label));
    this.Toaster.success('Files ready for submission');
    this.imageUploadLoading = false;
    this.clearSelectedFiles();
    this.uploadModalRef?.close();
  }

  private emitFileUploaded(newUrls: string[], newLabels: string[]) {
    this.fileUploaded.emit({
      newUrls,
      allUrls: [...this.existingUploadedUrls],
      newLabels,
      allLabels: this.existingUploadedUrls.map(u => this.pendingNewLabelsByUrl[u] || '')
    });
  }

  // Reads attachments already saved against masterId from both ImageMaster
  // and DocMaster (via MasterAttachmentService) and refreshes the visible
  // list. Called whenever the upload modal opens on an existing record, and
  // again after any save/delete so ids stay in sync with the server.
  private loadExistingAttachments() {
    this.masterAttachmentService.readAttachments(
      this.masterType,
      this.masterId,
      this.User.companyId,
      this.User.loggedInBranchId
    ).subscribe({
      next: (attachments) => {
        this.existingAttachments = attachments;
        this.existingUploadedUrls = attachments.map(a => a.url);
        this.existingLabels = attachments.map(a => a.label || '');
        this.existingChecked = attachments.map(() => true);
        this.editingExistingIndex = null;
      },
      error: (err) => console.error('Failed to load existing attachments', err)
    });
  }

  // Called by the parent once it has saved the underlying master record and
  // knows its real id (e.g. right after CompanyRelation_Create resolves).
  // Persists whatever was queued while masterId was still 0. Safe to call
  // even when nothing is queued, or when masterId was already known and
  // everything was already saved as it was added — the diff inside
  // MasterAttachmentService is a no-op in that case.
  commitPendingAttachments(newMasterId: number): Observable<any[]> {
    if (!this.masterType) return of([]);
    return this.masterAttachmentService.saveAttachmentChanges(
      this.masterType,
      newMasterId,
      this.existingUploadedUrls,
      this.existingAttachments,
      this.User.companyId,
      this.User.loggedInBranchId,
      this.User.userId.toString(),
      this.pendingNewLabelsByUrl
    );
  }

  clearSelectedFiles() {
    this.selectedFiles = [];
    this.selectedFilePreviews = [];
    this.selectedFileLabels = [];
    this.selectedFileChecked = [];
  }

  viewFile(index: number) {
    const file = this.selectedFiles[index];
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
  }

  viewExistingFile(url: string) {
  window.open(url, '_blank');
}

  // Confirms before touching anything - an already-saved file's delete is
  // immediate and permanent (see below), so an accidental click here can't
  // be undone the way removing a not-yet-uploaded queued file can.
  removeExistingFile(index: number) {
    const displayName = this.existingLabels[index] || this.existingAttachments[index]?.name || `file ${index + 1}`;
    this.confirmModal.openDeleteModal(displayName, true).subscribe(confirmed => {
      if (!confirmed) return;
      this.performRemoveExistingFile(index);
    });
  }

  private performRemoveExistingFile(index: number) {
    const [removedUrl] = this.existingUploadedUrls.splice(index, 1);
    this.existingLabels.splice(index, 1);
    this.existingChecked.splice(index, 1);
    if (this.editingExistingIndex === index) this.editingExistingIndex = null;
    delete this.pendingNewLabelsByUrl[removedUrl];

    if (this.masterId) {
      // Already-saved record - delete from whichever table (ImageMaster or
      // DocMaster) this file actually lives in, right away.
      this.masterAttachmentService.saveAttachmentChanges(
        this.masterType,
        this.masterId,
        [...this.existingUploadedUrls],
        this.existingAttachments,
        this.User.companyId,
        this.User.loggedInBranchId,
        this.User.userId.toString()
      ).subscribe({
        next: () => this.loadExistingAttachments(),
        error: (err) => console.error('Failed to delete attachment', err)
      });
    }
  }

  toggleEditExisting(index: number) {
    this.editingExistingIndex = this.editingExistingIndex === index ? null : index;
  }

  // Cancel button shown next to an existing row's Document Name field while
  // editing - reverts to whatever was actually saved, discarding the typed
  // change, instead of leaving it half-edited in the input.
  cancelEditExisting(index: number) {
    this.existingLabels[index] = this.existingAttachments[index]?.label || '';
    this.editingExistingIndex = null;
  }

  // Renaming an existing file's Document Name no longer saves per-row (see
  // cancelEditExisting() above) - every changed label is batched here and
  // only persisted when the modal's own Submit ("Save Changes"/"Upload New")
  // runs, via MasterAttachmentService.updateAttachmentLabel (ImageMaster.imgTitle
  // / DocMaster.docDesc). Compares against existingAttachments[i].label (what
  // the server actually has) so untouched rows never trigger a no-op Update.
  private saveChangedExistingLabels(): Observable<any> {
    if (!this.masterId) return of(null);

    const changed = this.existingAttachments
      .map((attachment, i) => ({ attachment, newLabel: this.existingLabels[i] || '' }))
      .filter(({ attachment, newLabel }) => (attachment.label || '') !== newLabel);

    if (changed.length === 0) return of(null);

    return forkJoin(changed.map(({ attachment, newLabel }) =>
      this.masterAttachmentService.updateAttachmentLabel(
        attachment,
        newLabel,
        this.User.companyId,
        this.User.loggedInBranchId,
        this.User.userId.toString()
      )
    ));
  }

  get totalFilesCount(): number {
    return (this.existingUploadedUrls?.length || 0) + (this.selectedFiles?.length || 0);
  }

  getFileTypeFromUrl(url: string): string {
    const ext = url.split('.').pop()?.toLowerCase();

    if (!ext) return 'other';

    if (ext === 'pdf') return 'pdf';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return 'image';

    return 'other';
  }

  handleModalClose() {
    this.resetUploadState();
    this.uploadModalRef?.close();
  }

  resetUploadState() {
    this.selectedFiles = [];
    this.selectedFilePreviews = [];
    this.uploadedUrls = [];
    this.imageUploadLoading = false;
  }
}
